import {IconButton} from '@dropbox/dig-components/buttons';
import {Checkbox} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine, MoreHorizontalLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {type MouseEvent} from 'react';
import {JiraTicketTabId} from 'views/home/dashboard/dashboardModel';

import {
  isJiraStatusEnabled,
  isJiraStatusFilterAtDefaults,
  JiraStatusFilterKey,
  JiraStatusFilterState,
} from './jiraStatusFilter';
import {ASSIGNED_TAB_ID, CREATED_TAB_ID} from './jiraTicketTabs';

/** Keep the menu above dashboard chrome (sticky search is z-index 4). */
const JIRA_MENU_Z_INDEX = 20;

const submenuTrigger = (label: string, value: string) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    style={{width: '100%', minWidth: 0, minHeight: 24, gap: 12}}
  >
    <Box style={{flex: 1, minWidth: 0}}>
      <Text>{label}</Text>
    </Box>
    <Box display="flex" alignItems="center" style={{flexShrink: 0}}>
      <Text size="small" color="subtle">
        {value}
      </Text>
    </Box>
  </Box>
);

const submenuChevron = <UIIcon src={ChevronRightLine} className={atoms({color: 'Text Subtle'})} />;

export const JiraTileMenu = ({
  statusFilter,
  availableStatuses,
  onToggleStatus,
  showTabs,
  defaultTab,
  onDefaultTabChange,
  onMenuOpen,
}: {
  statusFilter: JiraStatusFilterState;
  availableStatuses: JiraStatusFilterKey[];
  onToggleStatus: (status: JiraStatusFilterKey) => void;
  /** Whether the tile is currently showing Assigned/Created tabs. */
  showTabs: boolean;
  defaultTab: JiraTicketTabId;
  onDefaultTabChange: (tab: JiraTicketTabId) => void;
  onMenuOpen?: () => void;
}) => {
  if (availableStatuses.length === 0 && !showTabs) {
    return null;
  }

  const statusLabel = isJiraStatusFilterAtDefaults(statusFilter, availableStatuses)
    ? t('dashboard_jira_status_default')
    : t('dashboard_jira_status_count', {
        enabled: availableStatuses.filter((status) => isJiraStatusEnabled(statusFilter, status))
          .length,
        total: availableStatuses.length,
      });

  const defaultTabLabel =
    defaultTab === CREATED_TAB_ID
      ? t('dashboard_jira_tab_created')
      : t('dashboard_jira_tab_assigned');

  return (
    <Menu.Wrapper closeOnSelection={false} shouldPropagateClickOutsideMouseEvents>
      {({getContentProps, getTriggerProps}) => {
        const triggerProps = getTriggerProps();
        return (
          <>
            <IconButton
              {...triggerProps}
              variant="borderless"
              size="small"
              aria-label={t('dashboard_jira_menu_aria').toString()}
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                triggerProps.onClick?.(e);
                onMenuOpen?.();
              }}
              onMouseDown={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
              }}
            >
              <UIIcon src={MoreHorizontalLine} className={atoms({color: 'Text Subtle'})} />
            </IconButton>
            <Menu.Content
              {...getContentProps()}
              minWidth="220px"
              placement="top"
              positioningStrategy="fixed"
              style={{zIndex: JIRA_MENU_Z_INDEX}}
            >
              <Menu.Segment>
                {showTabs ? (
                  <Menu.Submenu
                    withTriggerContent={submenuTrigger(
                      t('dashboard_jira_menu_default_tab').toString(),
                      defaultTabLabel.toString()
                    )}
                    withRightAccessory={submenuChevron}
                    closeOnSelection={false}
                    contentProps={{style: {zIndex: JIRA_MENU_Z_INDEX}}}
                  >
                    <Menu.Segment>
                      <Menu.SelectItem
                        selected={defaultTab === ASSIGNED_TAB_ID}
                        onClick={() => onDefaultTabChange(ASSIGNED_TAB_ID)}
                      >
                        {t('dashboard_jira_tab_assigned')}
                      </Menu.SelectItem>
                      <Menu.SelectItem
                        selected={defaultTab === CREATED_TAB_ID}
                        onClick={() => onDefaultTabChange(CREATED_TAB_ID)}
                      >
                        {t('dashboard_jira_tab_created')}
                      </Menu.SelectItem>
                    </Menu.Segment>
                  </Menu.Submenu>
                ) : null}
                {availableStatuses.length > 0 ? (
                  <Menu.Submenu
                    withTriggerContent={submenuTrigger(
                      t('dashboard_jira_menu_status').toString(),
                      statusLabel.toString()
                    )}
                    withRightAccessory={submenuChevron}
                    closeOnSelection={false}
                    contentProps={{style: {zIndex: JIRA_MENU_Z_INDEX}}}
                  >
                    <Menu.Segment>
                      {availableStatuses.map((status) => {
                        const isEnabled = isJiraStatusEnabled(statusFilter, status);
                        return (
                          <Menu.ActionItem
                            key={status}
                            onClick={() => onToggleStatus(status)}
                            withLeftAccessory={
                              <Checkbox checked={isEnabled} readOnly tabIndex={-1} />
                            }
                          >
                            {t(status)}
                          </Menu.ActionItem>
                        );
                      })}
                    </Menu.Segment>
                  </Menu.Submenu>
                ) : null}
              </Menu.Segment>
            </Menu.Content>
          </>
        );
      }}
    </Menu.Wrapper>
  );
};
